import { ReactNode, useCallback, useMemo, useState } from 'react';
import { Box, IconButton, Skeleton, Stack, Typography } from '@mui/material';
import {
    DataGridPro,
    GridColDef,
    GridColumnVisibilityModel,
    GridFilterInputSingleSelect,
    GridFilterOperator,
    GridPaginationModel,
    GridSortModel,
    GridToolbarContainer,
    useGridApiContext,
} from '@mui/x-data-grid-pro';
import Iconify from 'src/components/iconify';
import CustomTooltip from 'src/components/visibility/components/CustomTooltip';
import type { InfringementRow } from 'src/context/api/brandInfringement/api';
import {
    ActiveDaysGaugeCell,
    PaidPresenceRateCell,
    PLACE_OPTIONS,
    placeLabels,
    presenceDisplayValue,
    PlaceChipsCell,
    SearchTermsCell,
    StatusActionCell,
} from './cells';

const DEGRADED_STATUSES = ['not_eligible', 'no_longer_active'];

const ROW_HEIGHT = 52;

const isDegraded = (row: InfringementRow) => DEGRADED_STATUSES.includes(row.status);

// A row can infringe in several places at once, so its value is a list of labels and equality is
// the wrong test: "is Title" has to match a row showing both Title and Title + Description. MUI's
// own singleSelect operators compare with `===`, which silently matched nothing here.
const placeFilterOperators: GridFilterOperator[] = [
    {
        label: 'is',
        value: 'is',
        getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) return null;
            return (value) => Array.isArray(value) && value.includes(filterItem.value);
        },
        InputComponent: GridFilterInputSingleSelect,
    },
    {
        label: 'is not',
        value: 'not',
        getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) return null;
            return (value) => !(Array.isArray(value) && value.includes(filterItem.value));
        },
        InputComponent: GridFilterInputSingleSelect,
    },
];

type InfringementRowWithId = InfringementRow & { __rowId: string };

type Props = {
    rows: InfringementRow[];
    loading: boolean;
    onTermClick: (term: string, row?: InfringementRow) => void;
    onStartCapture: (row: InfringementRow) => void;
    onDownloadDomainCsv: (row: InfringementRow) => void;
    onExportAll: () => void;
    rightSlot?: ReactNode;
    busyDomain?: string | null;
    /** Swaps the whole card for a skeleton block instead of showing a spinner over the grid. */
    showSkeleton?: boolean;
};

// Toolbar (44) + column headers (40) + a full page of 10 rows (520) + footer (40).
const CARD_HEIGHT = 644;

const iconButtonSx = {
    width: 26,
    height: 26,
    padding: 0,
    color: '#919EAB',
    '&:hover': { backgroundColor: 'action.hover' },
} as const;

function CustomToolbar({ onExportAll, rightSlot }: { onExportAll: () => void; rightSlot?: ReactNode }) {
    const apiRef = useGridApiContext();
    return (
        <GridToolbarContainer
            // The frame is flush, so the toolbar carries its own gutter (like the Non Branded grids).
            // The theme gives every toolbarContainer a dashed bottom border and a neutral background
            // (overrides/components/data-grid.tsx); with the `mb` below, that border floated as a
            // second rule just above the column headers. Both are dropped here.
            sx={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                p: 0, px: 2, pt: '10px', mb: 1,
                border: 'none', backgroundColor: 'transparent',
            }}
        >
            <Stack direction="row" spacing={0.5}>
                <CustomTooltip title="Select columns" arrow placement="top">
                    <IconButton onClick={() => apiRef.current?.showPreferences('columns' as any)} sx={iconButtonSx}>
                        <Iconify icon="mingcute:calendar-week-line" width={18} height={18} />
                    </IconButton>
                </CustomTooltip>
                <CustomTooltip title="Filters" arrow placement="top">
                    <IconButton onClick={() => apiRef.current?.showFilterPanel()} sx={iconButtonSx}>
                        <Iconify icon="ic:round-filter-list" width={18} height={18} />
                    </IconButton>
                </CustomTooltip>
                <CustomTooltip title="Download" arrow placement="top">
                    <IconButton onClick={onExportAll} sx={iconButtonSx}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path
                                d="M14 10V10.8C14 11.9201 14 12.4802 13.782 12.908C13.5903 13.2843 13.2843 13.5903 12.908 13.782C12.4802 14 11.9201 14 10.8 14H5.2C4.07989 14 3.51984 14 3.09202 13.782C2.71569 13.5903 2.40973 13.2843 2.21799 12.908C2 12.4802 2 11.9201 2 10.8V10M4.66667 6.66667L8 10L11.3333 6.66667M8 10V2"
                                stroke="#919EAB"
                                strokeWidth="1.4"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </IconButton>
                </CustomTooltip>
            </Stack>
            {rightSlot}
        </GridToolbarContainer>
    );
}

export default function DataGridBrandInfringement({
    rows,
    loading,
    onTermClick,
    onStartCapture,
    onDownloadDomainCsv,
    onExportAll,
    rightSlot,
    busyDomain = null,
    showSkeleton = false,
}: Props) {
    const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({});

    const handleColumnVisibilityModelChange = useCallback((newModel: GridColumnVisibilityModel) => {
        setColumnVisibilityModel(newModel);
    }, []);

    const [sortModel, setSortModel] = useState<GridSortModel>([
        { field: 'paid_presence_rate', sort: 'desc' },
    ]);

    // Controlled so the loading skeleton can be sized to a full page of rows.
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
        pageSize: 10,
        page: 0,
    });

    const rowsWithId = useMemo(
        () => rows.map((row, index) => ({ ...row, __rowId: `${row.domain_name}::${index}` })),
        [rows]
    );

    const columns: GridColDef<InfringementRowWithId>[] = useMemo(() => [
        {
            field: 'domain_name',
            headerName: 'Infringing domains',
            flex: 1,
            minWidth: 180,
            renderCell: ({ row }) => (
                <Typography
                    variant="body2"
                    fontWeight={500}
                    noWrap
                    sx={{ color: isDegraded(row) ? 'rgba(145,158,171,0.8)' : '#333c47' }}
                >
                    {row.domain_name}
                </Typography>
            ),
        },
        {
            field: 'paid_presence_rate',
            headerName: 'Paid Presence Rate',
            width: 196,
            // Untyped, MUI filters this column with its *string* operators: `equals 97` compared
            // "97" against the raw 96.8 behind a cell reading "97%" and matched nothing, and the
            // numeric comparisons weren't even on offer. `type` restores them; the valueGetter
            // makes them compare against the integer on screen.
            type: 'number',
            // `type: 'number'` right-aligns a column by default, and this one is a value plus a
            // progress bar that reads left to right. Pinned back to where it was.
            align: 'left',
            headerAlign: 'left',
            valueGetter: (_value, row) => (row.paid_presence_rate == null || isDegraded(row))
                ? null
                : presenceDisplayValue(row.paid_presence_rate),
            renderCell: ({ row }) => (row.paid_presence_rate == null || isDegraded(row))
                ? null
                : <PaidPresenceRateCell value={row.paid_presence_rate} />,
        },
        {
            field: 'active_days',
            headerName: 'Active days',
            width: 120,
            // Same reason as the rate above: without it these filter as text.
            type: 'number',
            align: 'right',
            headerAlign: 'right',
            renderCell: ({ row }) => (row.active_days == null || isDegraded(row))
                ? null
                : <ActiveDaysGaugeCell activeDays={row.active_days} totalDays={row.total_days ?? row.days} />,
        },
        {
            field: 'unique_ads',
            headerName: 'Ads infringed',
            width: 132,
            type: 'number',
            align: 'right',
            headerAlign: 'right',
            renderCell: ({ row }) => (row.unique_ads == null || isDegraded(row))
                ? null
                : <Typography variant="body2" fontWeight={500}>{row.unique_ads.toLocaleString()}</Typography>,
        },
        {
            field: 'place',
            headerName: 'Place',
            width: 229,
            sortable: false,
            // There is no `place` on the row: the chips are derived from the three *_count fields.
            // Without a valueGetter MUI resolved `row.place` to `undefined` and every filter on
            // this column matched zero rows.
            valueGetter: (_value, row) => isDegraded(row) ? [] : placeLabels({
                titleOnly: row.title_only_count ?? 0,
                descOnly: row.desc_only_count ?? 0,
                both: row.both_count ?? 0,
            }),
            valueOptions: PLACE_OPTIONS as unknown as string[],
            filterOperators: placeFilterOperators,
            renderCell: ({ row }) => isDegraded(row) ? null : (
                <PlaceChipsCell
                    titleOnly={row.title_only_count ?? 0}
                    descOnly={row.desc_only_count ?? 0}
                    both={row.both_count ?? 0}
                />
            ),
        },
        {
            field: 'search_terms',
            headerName: 'Search terms',
            flex: 2,
            minWidth: 280,
            sortable: false,
            renderCell: ({ row }) => isDegraded(row) ? null : (
                <SearchTermsCell terms={row.search_terms ?? []} onTermClick={(term) => onTermClick(term, row)} />
            ),
        },
        {
            field: 'status',
            headerName: 'Status & Action',
            width: 192,
            sortable: true,
            renderCell: ({ row }) => (
                <StatusActionCell
                    status={row.status}
                    busy={busyDomain === row.domain_name}
                    onStartCapture={() => onStartCapture(row)}
                    onDownload={() => onDownloadDomainCsv(row)}
                />
            ),
        },
    ], [onTermClick, onStartCapture, onDownloadDomainCsv, busyDomain]);

    // Placed after every hook, so switching to the skeleton does NOT unmount this component: the
    // column visibility, sort and pagination models above survive a refetch untouched. Only the
    // grid subtree below goes away, which has no state of its own worth keeping.
    if (showSkeleton) {
        return (
            <Skeleton
                variant="rectangular"
                animation="wave"
                width="100%"
                height={CARD_HEIGHT}
                sx={{ borderRadius: '12px' }}
            />
        );
    }

    return (
        <Box
            // Frame with no padding of its own: like the other Signal grids, the toolbar, column
            // headers, rows and pagination all run edge to edge and carry their own insets.
            // `overflow: hidden` clips the grid's corners to the frame radius.
            bgcolor="white"
            sx={{
                width: '100%',
                border: '1px solid rgba(145, 158, 171, 0.20)',
                borderRadius: '12px',
                overflow: 'hidden',
                '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus': {
                    outline: 'none !important',
                },
                '& .MuiDataGrid-cell': {
                    display: 'flex',
                    alignItems: 'center',
                    // 20px to match the column headers below, so a title sits exactly above its
                    // own column's content (MUI's default is 10px on both).
                    px: '20px',
                    borderRight: '1px solid rgba(145,158,171,0.2)',
                    // The theme paints cell bottom borders dashed (overrides/components/data-grid.tsx),
                    // which clashed with the solid vertical separators. Explore overrides it the same way.
                    borderBottom: '1px solid rgba(145,158,171,0.2)',
                },
                // The chips in this column deep-link into Ads, so the cell announces itself on
                // hover. Scoped to the cell rather than the row: the rest of the row isn't
                // clickable. The tint is translucent, so it composites over the white row.
                '& .MuiDataGrid-cell[data-field="search_terms"]:hover': {
                    backgroundColor: 'rgba(226,229,233,0.16)',
                },
                // v7 closes every row with a filler div that carries the `cell` class (GridRow.js),
                // and adds a scrollbar filler beside the headers. Left alone they inherit the border
                // rules above and draw a stray bordered box past the last column.
                '& .MuiDataGrid-cellEmpty, & .MuiDataGrid-filler, & .MuiDataGrid-scrollbarFiller': {
                    border: 'none',
                },
                // The frame is flush against the grid now, so the last column's own right border
                // would sit one pixel from the frame border and read as a double line. Anchored on
                // the field rather than `:last-of-type`, which matches those fillers, not a column.
                '& .MuiDataGrid-cell[data-field="status"], & .MuiDataGrid-columnHeader[data-field="status"]': {
                    borderRight: 'none',
                },
                // v7: l'en-tête est peint via ces variables CSS, `.MuiDataGrid-columnHeaders` n'a aucun effet.
                '& .MuiDataGrid-root': {
                    '--DataGrid-containerBackground': 'rgba(226,229,233,0.16)',
                },
                // The global data-grid-custom.css forces `white-space: normal !important` on all
                // headers; override it locally so the titles stay on a single line.
                '& .MuiDataGrid-columnHeaderTitle': {
                    color: '#637381',
                    fontSize: 14,
                    fontWeight: 600,
                    lineHeight: '16.8px',
                    whiteSpace: 'nowrap !important',
                    overflow: 'visible',
                },
                // 40px flat, whatever the title does: `columnHeaderHeight` sizes the header row but
                // the header cell itself still grew with its content (the global
                // `white-space: normal` in data-grid-custom.css lets a long title wrap onto a
                // second line), which is what pushed it to 46px. Pinned here, with the 8px/20px
                // insets from the design.
                '& .MuiDataGrid-columnHeader': {
                    height: 40,
                    minHeight: 40,
                    maxHeight: 40,
                    boxSizing: 'border-box',
                    padding: '8px 20px',
                    borderRight: '1px solid rgba(145,158,171,0.2)',
                },
                // MUI drops its own horizontal padding into this inner container on some header
                // variants (sortable/filterable), which would add to the 20px above.
                '& .MuiDataGrid-columnHeaderDraggableContainer, & .MuiDataGrid-columnHeaderTitleContainer': {
                    padding: 0,
                },
                // MUI's footer defaults to 52px (its TablePagination toolbar carries its own
                // min-height), which left it taller than the 40px header. Pin both containers so
                // header and footer match the rest of the platform's grids.
                '& .MuiDataGrid-footerContainer': {
                    minHeight: 40,
                    height: 40,
                    // Same tint as the column headers, so the table is bracketed top and bottom
                    // like the Performance Split and Explore grids. No border of its own: the last
                    // row's bottom border already closes the grid, a second one reads as a double rule.
                    backgroundColor: 'rgba(226,229,233,0.16)',
                    borderTop: 'none',
                    '& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select, & .MuiTablePagination-input': {
                        color: '#919EAB',
                        fontSize: 14,
                    },
                },
                '& .MuiDataGrid-footerContainer .MuiToolbar-root': {
                    minHeight: 40,
                },
            }}
        >
            <DataGridPro
                rows={rowsWithId}
                columns={columns}
                getRowId={(row) => row.__rowId}
                loading={loading}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
                sortModel={sortModel}
                onSortModelChange={setSortModel}
                columnHeaderHeight={40}
                rowHeight={ROW_HEIGHT}
                pagination
                pageSizeOptions={[10, 25, 50]}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                // The first load is covered by a card-sized skeleton in the page, so the grid only
                // ever renders with data here and MUI's default overlay is enough for refetches.
                slots={{ toolbar: CustomToolbar as any }}
                slotProps={{ toolbar: { onExportAll, rightSlot } as any }}
                disableRowSelectionOnClick
                autoHeight
            />
        </Box>
    );
}
